import type { Data } from '@bulknutrients/bulk-shared-lib/interfaces/LongTailPage';
import {
  getAllLongTailSlugs,
  getLongTailData,
  getLongTailPreviewData,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/longTailData';
import { getRedirect } from '@bulknutrients/bulk-shared-lib/lib/sanity/redirect';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Container } from '@components/Container';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { ProductCard } from '@components/ProductCard/ProductCard';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import type { ReactElement } from 'react';

interface Props {
  pageData: Data;
  isPreview: boolean;
}
export default function LongTailPage({
  pageData,
  isPreview,
}: Props): ReactElement {
  return (
    <>
      <Head>
        <title>{pageData.metaTitle}</title>
        <meta name="title" content={pageData.metaTitle} />
        <meta name="description" content={pageData.metaDescription} />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}${pageData.slug}`}
        />
      </Head>
      <Container>
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            { title: 'Buy', path: '/buy' },
            { title: pageData.title, path: pageData.slug },
          ]}
        />
      </Container>
      <Container as="main" id="main-content">
        <div className="justify-self-center w-narrow-page">
          <h1>{pageData.title}</h1>
          <PortableTextBlockRenderer content={pageData.bodyText} />
        </div>
        <section
          data-testid="product-list"
          className="grid grid-cols-1 gap-9 sm:grid-cols-2 lg:grid-cols-3"
        >
          {pageData.products.map((product) => (
            <ProductCard
              key={product.entityId}
              product={product}
              location={`Buy long tail page ${pageData.slug}`}
            />
          ))}
        </section>
      </Container>
    </>
  );
}

LongTailPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = await getAllLongTailSlugs();

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({
  params,
  preview,
  previewData,
}) => {
  try {
    let pageData: Data;
    if (preview) {
      // This is preview mode
      pageData = await getLongTailPreviewData(
        (previewData as { id: string }).id
      );
    } else {
      pageData = await getLongTailData(params?.slug?.toString() as string);
    }

    if (!pageData) {
      // try redirect
      const redirect = await getRedirect(
        `/buy/${params?.slug?.toString().split(',').join('/')}` as string
      );

      if (redirect) {
        return {
          redirect: {
            destination: redirect.newUrl,
            permanent: redirect.permanent,
          },
        };
      }

      // fallback failed
      return {
        notFound: true,
      };
    }

    return {
      props: {
        pageData,
        isPreview: !!preview,
      },
    };
  } catch (error) {
    console.error(error);
    return {
      props: {
        pageData: {},
      },
    };
  }
};
